import { FC, useEffect, useMemo } from "react";
import { MRT_ColumnDef } from "material-react-table";
import dayjs from "dayjs";
import { Box } from "@mui/material";
import { Table } from "@/components/elements/Table";
import { CenteredCell } from "@/components/commons/CenteredCell";
import { EMPTY_STR } from "@/constants/string";
import { divider } from "@/theme/colors";
import { JobStatusTag } from "@/features/job/components/JobStatusTag";
import { TJobDisplayColumnSetting } from "@/features/job/components/job-list/job-filter-shared";
import { TJobListOutputDto } from "@/api/services/main/job/dtos/get-job-list.dto";
import { useEnum } from "@/api/services/global/enum";
import { useTable } from "@/hooks/useTable";
import { findLabelByValue } from "@/utils/object";
import { truncateWithEllipsis } from "@/utils/string";
import { AppRoutes } from "@/routes/config";
import { getDynamicRoute } from "@/utils/url";
import { DATE_FORMAT } from "@/constants/datetime";

type TPastJobsTableProps = {
  rows: TJobListOutputDto[];
  isLoading: boolean;
  displayColumnSettings?: TJobDisplayColumnSetting[] | null;
};

const CLOSED_STATUS = "closed";

const MAX_CELL_TEXT_LENGTH = 25;
const MAX_WORKPLACE_TEXT_LENGTH = 15;

const truncateText = (value: string | null | undefined): string => (value ? truncateWithEllipsis(value, MAX_CELL_TEXT_LENGTH) : EMPTY_STR.TEXT);

const truncateWorkplaceText = (value: string | null | undefined): string =>
  value ? truncateWithEllipsis(value, MAX_WORKPLACE_TEXT_LENGTH) : EMPTY_STR.TEXT;

const formatDate = (value?: unknown) => (value ? dayjs(value as never).format(DATE_FORMAT) : EMPTY_STR.DATE);

const formatCurrency = (value?: number | null) => (value != null ? `${value.toLocaleString()}円` : EMPTY_STR.TEXT);

const getDeadlineDate = (row: TJobListOutputDto): unknown => {
  const endDate = row.endDate;
  if (row.status === CLOSED_STATUS && row.closedAt) {
    if (!endDate || dayjs(row.closedAt).isBefore(dayjs(endDate))) {
      return row.closedAt;
    }
  }
  return endDate;
};

export const PastJobsTable: FC<TPastJobsTableProps> = ({ rows, isLoading, displayColumnSettings }) => {
  const { data: jobStatusOptions } = useEnum("EJobStatus");

  const columns = useMemo<MRT_ColumnDef<TJobListOutputDto>[]>(
    () => [
      {
        id: "jobName",
        header: "求人名",
        accessorKey: "jobName",
        minSize: 180,
        Cell: ({ row }) => truncateText(row.original.jobName),
      },
      {
        id: "status",
        header: "ステータス",
        accessorKey: "status",
        minSize: 120,
        Cell: ({ row }) => (
          <CenteredCell>
            <JobStatusTag status={row.original.status} title={findLabelByValue(jobStatusOptions, row.original.status)} variant="bold" />
          </CenteredCell>
        ),
      },
      {
        id: "totalAmount",
        header: "総支給額",
        minSize: 110,
        Cell: ({ row }) => formatCurrency(row.original.jobSalarySummaries?.[0]?.totalAmount),
      },
      {
        id: "workplace",
        header: "勤務場所",
        accessorKey: "workplace",
        minSize: 140,
        Cell: ({ row }) => truncateWorkplaceText(row.original.workplace),
      },
      {
        id: "caseCount",
        header: "応募数",
        minSize: 86,
        Cell: ({ row }) => {
          const count = row.original.caseCount ?? 0;
          return `${count}人`;
        },
      },
      {
        id: "recruitmentNumber",
        header: "募集人数",
        minSize: 86,
        Cell: ({ row }) => (row.original.recruitmentNumber != null ? `${row.original.recruitmentNumber}人` : EMPTY_STR.TEXT),
      },
      {
        id: "endDate",
        header: "募集期限",
        minSize: 110,
        Cell: ({ row }) => formatDate(getDeadlineDate(row.original)),
      },
    ],
    [jobStatusOptions],
  );

  const handleRowClick = (row: TJobListOutputDto) => {
    const route = getDynamicRoute(AppRoutes.jobDetail, { jobId: String(row.id) });
    window.open(route, "_blank", "noopener,noreferrer");
  };

  const { table } = useTable({
    columns,
    rows,
    defaultColumn: {
      minSize: 50,
      maxSize: Number.MAX_VALUE,
      size: 1,
    },
    onClickRow: (row) => handleRowClick(row),
    renderEmptyRowsFallback: () => <Box sx={{ height: 80 }} />,
  });

  useEffect(() => {
    if (!displayColumnSettings) return;

    const visibleIds = displayColumnSettings.filter((s) => s.visible).map((s) => s.id);
    table.setColumnOrder(visibleIds);

    const visibilityMap: Record<string, boolean> = {};
    displayColumnSettings.forEach((s) => {
      visibilityMap[s.id] = s.visible;
    });
    table.setColumnVisibility(visibilityMap);
  }, [displayColumnSettings, table]);

  return <Table table={table} isLoading={isLoading} maxHeight={400} headRowHeight={40} bodyRowHeight={48} borderColor={divider.high} />;
};
